<div class="container my-5">
  <div class="row justify-content-center">
    <div class="col-lg-8">

      <h1 class="display-6 fw-bold mb-2">Privacy Policy</h1>
      <p class="text-body-secondary mb-4">Last updated: March 2026</p>
      <hr>

      <section class="mb-4">
        <h2 class="h5 fw-semibold">1. Introduction</h2>
        <p>Welcome to Vocabulary ("we", "us", "our"). We are committed to protecting your personal data in accordance with the <a href="https://gdpr-info.eu/" target="_blank" rel="noopener">EU General Data Protection Regulation (GDPR)</a> and the <a href="https://www.fedlex.admin.ch/eli/cc/2022/491/en" target="_blank" rel="noopener">Swiss Federal Act on Data Protection (FADP)</a>. This policy explains how we collect, process, and safeguard your information when you use our vocabulary learning application.</p>
      </section>

      <section class="mb-4">
        <h2 class="h5 fw-semibold">2. Data Controller</h2>
        <p>The data controller responsible for the processing of your personal data is the operator of Vocabulary. For questions regarding data protection, please contact us at <a href="mailto:support@myvocabulary.net?subject=Privacy%20Policy">support&#64;myvocabulary.net</a>.</p>
      </section>

      <section class="mb-4">
        <h2 class="h5 fw-semibold">3. Legal Basis for Processing</h2>
        <p>We process your personal data on the following legal bases under <a href="https://gdpr-info.eu/art-6-gdpr/" target="_blank" rel="noopener">Article 6 GDPR</a>:</p>
        <ul class="list-group list-group-flush mb-3">
          <li class="list-group-item"><strong>Contract performance (<a href="https://gdpr-info.eu/art-6-gdpr/" target="_blank" rel="noopener">Art. 6(1)(b)</a>):</strong> processing necessary to provide the vocabulary learning service you registered for.</li>
          <li class="list-group-item"><strong>Consent (<a href="https://gdpr-info.eu/art-6-gdpr/" target="_blank" rel="noopener">Art. 6(1)(a)</a>):</strong> where you have given explicit consent, such as accepting analytics cookies. You may withdraw your consent at any time via the cookie banner.</li>
          <li class="list-group-item"><strong>Legitimate interests (<a href="https://gdpr-info.eu/art-6-gdpr/" target="_blank" rel="noopener">Art. 6(1)(f)</a>):</strong> logging of IP addresses and user activity for security monitoring, fraud prevention, and operational stability.</li>
          <li class="list-group-item"><strong>Legal obligation (<a href="https://gdpr-info.eu/art-6-gdpr/" target="_blank" rel="noopener">Art. 6(1)(c)</a>):</strong> where we are required to retain data by applicable law.</li>
        </ul>
      </section>

      <section class="mb-4">
        <h2 class="h5 fw-semibold">4. Data We Collect</h2>
        <p>We collect the following categories of personal data:</p>
        <div class="row g-3">
          <div class="col-md-4">
            <div class="card h-100">
              <div class="card-body">
                <h6 class="card-title fw-semibold">Account data</h6>
                <p class="card-text small">Name, email address, and authentication credentials managed through our identity provider (Keycloak).</p>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card h-100">
              <div class="card-body">
                <h6 class="card-title fw-semibold">Usage data</h6>
                <p class="card-text small">Vocabulary lists, learning progress, language preferences, and interaction history within the application.</p>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card h-100">
              <div class="card-body">
                <h6 class="card-title fw-semibold">Log data</h6>
                <p class="card-text small">IP addresses, browser type, device information, access timestamps, and user activity logs collected for security and operations.</p>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card h-100">
              <div class="card-body">
                <h6 class="card-title fw-semibold">Analytics data</h6>
                <p class="card-text small">Anonymised usage data (pages visited, session duration, browser type) collected via Google Analytics only if you have given your consent.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="mb-4">
        <h2 class="h5 fw-semibold">5. How We Use Your Data</h2>
        <ul>
          <li>Providing and maintaining the vocabulary learning service.</li>
          <li>Authenticating users and managing sessions via Keycloak.</li>
          <li>Monitoring security threats, detecting unauthorised access, and preventing abuse.</li>
          <li>Diagnosing technical issues and ensuring operational stability.</li>
          <li>Improving the application and developing new features.</li>
        </ul>
      </section>

      <section class="mb-4">
        <h2 class="h5 fw-semibold">6. Data Sharing and Processors</h2>
        <p>We do not sell your personal data. Your data may be processed by the following categories of recipients:</p>
        <table class="table table-bordered">
          <thead class="table-light">
            <tr>
              <th>Processor</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Keycloak (identity provider)</td>
              <td>Authentication and account management</td>
            </tr>
            <tr>
              <td>Google LLC (Google Analytics)</td>
              <td>Anonymised usage analytics, only if you have given consent. Google may process data on servers outside the EEA. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener">Google's Privacy Policy</a>.</td>
            </tr>
            <tr>
              <td>Hosting providers</td>
              <td>Infrastructure services, subject to data processing agreements (<a href="https://gdpr-info.eu/art-28-gdpr/" target="_blank" rel="noopener">Art. 28 GDPR</a>)</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="mb-4">
        <h2 class="h5 fw-semibold">7. International Transfers</h2>
        <p>If your data is transferred outside the EU/EEA or Switzerland, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses (SCCs) or an adequacy decision by the European Commission.</p>
      </section>

      <section class="mb-4">
        <h2 class="h5 fw-semibold">8. Data Retention</h2>
        <ul class="list-group list-group-flush mb-3">
          <li class="list-group-item"><strong>Account and usage data:</strong> retained for as long as your account is active. Upon account deletion, your data is removed promptly.</li>
          <li class="list-group-item"><strong>Security and operational logs:</strong> retained for a limited period necessary to detect and investigate security incidents, after which they are deleted.</li>
        </ul>
      </section>

      <section class="mb-4">
        <h2 class="h5 fw-semibold">9. Your Rights Under GDPR</h2>
        <div class="row g-2">
          <div class="col-md-6">
            <div class="card h-100">
              <div class="card-body py-2">
                <p class="small mb-0"><strong>Access (<a href="https://gdpr-info.eu/art-15-gdpr/" target="_blank" rel="noopener">Art. 15</a>)</strong> &mdash; Obtain confirmation of whether your data is being processed and request a copy.</p>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card h-100">
              <div class="card-body py-2">
                <p class="small mb-0"><strong>Rectification (<a href="https://gdpr-info.eu/art-16-gdpr/" target="_blank" rel="noopener">Art. 16</a>)</strong> &mdash; Request correction of inaccurate data.</p>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card h-100">
              <div class="card-body py-2">
                <p class="small mb-0"><strong>Erasure (<a href="https://gdpr-info.eu/art-17-gdpr/" target="_blank" rel="noopener">Art. 17</a>)</strong> &mdash; Request deletion of your data. You can @if (auth.isAuthenticated()) {<a routerLink="/settings/delete-user">delete your account</a>} @else {delete your account} directly.</p>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card h-100">
              <div class="card-body py-2">
                <p class="small mb-0"><strong>Restriction (<a href="https://gdpr-info.eu/art-18-gdpr/" target="_blank" rel="noopener">Art. 18</a>)</strong> &mdash; Request restriction of processing under certain conditions.</p>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card h-100">
              <div class="card-body py-2">
                <p class="small mb-0"><strong>Portability (<a href="https://gdpr-info.eu/art-20-gdpr/" target="_blank" rel="noopener">Art. 20</a>)</strong> &mdash; Receive your data in a structured, machine-readable format.</p>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card h-100">
              <div class="card-body py-2">
                <p class="small mb-0"><strong>Object (<a href="https://gdpr-info.eu/art-21-gdpr/" target="_blank" rel="noopener">Art. 21</a>)</strong> &mdash; Object to processing based on legitimate interests.</p>
              </div>
            </div>
          </div>
        </div>
        <p class="mt-3">To exercise any of these rights, please contact us at <a href="mailto:support@myvocabulary.net?subject=Privacy%20Policy%20-%20Data%20Rights%20Request">support&#64;myvocabulary.net</a>. You also have the right to lodge a complaint with a supervisory authority.</p>
      </section>

      <section class="mb-4">
        <h2 class="h5 fw-semibold">10. Security</h2>
        <ul>
          <li>HTTPS encryption enforced for all data in transit.</li>
          <li>Secure authentication via Keycloak with industry-standard protocols.</li>
          <li>Access controls and monitoring to detect unauthorised access.</li>
        </ul>
      </section>

      <section>
        <h2 class="h5 fw-semibold">11. Contact</h2>
        <p>If you have questions about this privacy policy or wish to exercise your data protection rights, please contact us at <a href="mailto:support@myvocabulary.net?subject=Privacy%20Policy">support&#64;myvocabulary.net</a>.</p>
      </section>

    </div>
  </div>
</div>
